import { PageLayout } from '@/layouts'
import { Card, CardTitle, CardDescription, LoadingSpinner, ErrorDisplay, HelpIcon } from '@/components/ui'
import { usePrivacyStats } from '@/hooks/use-privacy'
import {
  BarChart,
  Bar,
  PieChart,
  Pie,
  Cell,
  ResponsiveContainer,
  XAxis,
  YAxis,
  Tooltip as RechartsTooltip,
  Legend,
} from 'recharts'
import { Shield, Clock, CheckCircle, AlertTriangle, Users } from 'lucide-react'
import { STATUS_LABELS, VERIFICATION_STATUS_LABELS, JURISDICTION_LABELS } from '@/models/privacy'
import { PRIVACY_HELP } from '@/features/privacy/constants/help-content'

const COLORS = {
  status: {
    received: '#3B82F6', // blue
    in_progress: '#F59E0B', // amber
    completed: '#10B981', // green
    denied: '#EF4444', // red
  },
  verification: {
    pending: '#94A3B8', // slate
    verified: '#10B981', // green
    failed: '#EF4444', // red
  },
  jurisdiction: {
    ccpa: '#8B5CF6', // violet
    gdpr: '#6366F1', // indigo
    other: '#94A3B8', // slate
  },
  requestTypes: ['#3B82F6', '#F59E0B', '#EF4444', '#8B5CF6'], // blue, amber, red, violet
}

function StatCard({
  title,
  value,
  description,
  icon: Icon,
  color = 'primary',
  helpContent,
}: {
  title: string
  value: string | number
  description?: string
  icon: React.ComponentType<{ className?: string }>
  color?: 'primary' | 'green' | 'yellow' | 'red'
  helpContent?: string
}) {
  const colorStyles = {
    primary: 'text-primary-600 bg-primary-50 dark:bg-primary-900/20',
    green: 'text-green-600 bg-green-50 dark:bg-green-900/20',
    yellow: 'text-yellow-600 bg-yellow-50 dark:bg-yellow-900/20',
    red: 'text-red-600 bg-red-50 dark:bg-red-900/20',
  }

  return (
    <Card>
      <div className="flex items-start justify-between">
        <div>
          <div className="flex items-center gap-1">
            <CardDescription>{title}</CardDescription>
            {helpContent && <HelpIcon content={helpContent} size="sm" />}
          </div>
          <p className="mt-2 text-3xl font-bold text-slate-900 dark:text-white">{value}</p>
          {description && (
            <p className="mt-1 text-xs text-slate-500 dark:text-slate-400">{description}</p>
          )}
        </div>
        <div className={`p-2 rounded-lg ${colorStyles[color]}`}>
          <Icon className="h-6 w-6" />
        </div>
      </div>
    </Card>
  )
}

export function PrivacyStatsPage() {
  const { data: stats, isLoading, error, refetch } = usePrivacyStats()

  if (isLoading) {
    return (
      <PageLayout title="Privacy Statistics" description="Analytics and metrics for privacy requests">
        <div className="flex items-center justify-center py-12">
          <LoadingSpinner size="lg" />
        </div>
      </PageLayout>
    )
  }

  if (error || !stats) {
    return (
      <PageLayout title="Privacy Statistics" description="Analytics and metrics for privacy requests">
        <ErrorDisplay
          error={error as Error}
          title="Failed to Load Statistics"
          onRetry={() => refetch()}
        />
      </PageLayout>
    )
  }

  // Transform data for charts
  const statusData = Object.entries(stats.by_status).map(([key, value]) => ({
    name: STATUS_LABELS[key as keyof typeof STATUS_LABELS] || key,
    value,
    fill: COLORS.status[key as keyof typeof COLORS.status] || '#94A3B8',
  }))

  const verificationData = Object.entries(stats.by_verification).map(([key, value]) => ({
    name: VERIFICATION_STATUS_LABELS[key as keyof typeof VERIFICATION_STATUS_LABELS] || key,
    value,
    fill: COLORS.verification[key as keyof typeof COLORS.verification] || '#94A3B8',
  }))

  const jurisdictionData = Object.entries(stats.by_jurisdiction).map(([key, value]) => ({
    name: JURISDICTION_LABELS[key as keyof typeof JURISDICTION_LABELS] || key,
    value,
    fill: COLORS.jurisdiction[key as keyof typeof COLORS.jurisdiction] || '#94A3B8',
  }))

  const requestTypeData = [
    { name: 'Opt-Out Sale', value: stats.by_request_type.opt_out_sale },
    { name: 'Opt-Out Share', value: stats.by_request_type.opt_out_share },
    { name: 'Remove Data', value: stats.by_request_type.remove_data },
    { name: 'Do Not Contact', value: stats.by_request_type.do_not_contact },
  ]

  return (
    <PageLayout title="Privacy Statistics" description="Analytics and metrics for privacy requests">
      {/* KPI Cards */}
      <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-4 mb-8">
        <StatCard
          title="Total Subjects"
          value={stats.total}
          description="All privacy subjects"
          icon={Users}
          color="primary"
        />
        <StatCard
          title="Pending Verification"
          value={stats.by_verification.pending || 0}
          description="Awaiting verification"
          icon={Clock}
          color="yellow"
          helpContent={PRIVACY_HELP.stats.pendingVerification}
        />
        <StatCard
          title="Due Soon"
          value={stats.pending_due_soon}
          description="Due within 7 days"
          icon={AlertTriangle}
          color="yellow"
          helpContent={PRIVACY_HELP.stats.dueSoon}
        />
        <StatCard
          title="Overdue"
          value={stats.overdue_count}
          description="Past due date"
          icon={AlertTriangle}
          color="red"
          helpContent={PRIVACY_HELP.stats.overdue}
        />
      </div>

      {/* Charts Row 1 */}
      <div className="grid gap-6 lg:grid-cols-2 mb-6">
        {/* Status Distribution */}
        <Card>
          <CardTitle>Status Distribution</CardTitle>
          <CardDescription>Breakdown by request status</CardDescription>
          <div className="h-64 mt-4">
            <ResponsiveContainer width="100%" height="100%">
              <PieChart>
                <Pie
                  data={statusData}
                  cx="50%"
                  cy="50%"
                  innerRadius={60}
                  outerRadius={90}
                  paddingAngle={2}
                  dataKey="value"
                  label={({ name, percent }) => `${name} ${(percent * 100).toFixed(0)}%`}
                  labelLine={false}
                >
                  {statusData.map((entry, index) => (
                    <Cell key={`cell-${index}`} fill={entry.fill} />
                  ))}
                </Pie>
                <RechartsTooltip
                  contentStyle={{
                    backgroundColor: 'rgba(255, 255, 255, 0.95)',
                    border: '1px solid #e2e8f0',
                    borderRadius: '8px',
                  }}
                />
                <Legend />
              </PieChart>
            </ResponsiveContainer>
          </div>
        </Card>

        {/* Verification Status */}
        <Card>
          <CardTitle>Verification Status</CardTitle>
          <CardDescription>Identity verification breakdown</CardDescription>
          <div className="h-64 mt-4">
            <ResponsiveContainer width="100%" height="100%">
              <PieChart>
                <Pie
                  data={verificationData}
                  cx="50%"
                  cy="50%"
                  innerRadius={60}
                  outerRadius={90}
                  paddingAngle={2}
                  dataKey="value"
                  label={({ name, percent }) => `${name} ${(percent * 100).toFixed(0)}%`}
                  labelLine={false}
                >
                  {verificationData.map((entry, index) => (
                    <Cell key={`cell-${index}`} fill={entry.fill} />
                  ))}
                </Pie>
                <RechartsTooltip
                  contentStyle={{
                    backgroundColor: 'rgba(255, 255, 255, 0.95)',
                    border: '1px solid #e2e8f0',
                    borderRadius: '8px',
                  }}
                />
                <Legend />
              </PieChart>
            </ResponsiveContainer>
          </div>
        </Card>
      </div>

      {/* Charts Row 2 */}
      <div className="grid gap-6 lg:grid-cols-2">
        {/* Jurisdiction Distribution */}
        <Card>
          <CardTitle>By Jurisdiction</CardTitle>
          <CardDescription>Requests by regulatory jurisdiction</CardDescription>
          <div className="h-64 mt-4">
            <ResponsiveContainer width="100%" height="100%">
              <BarChart data={jurisdictionData} layout="vertical">
                <XAxis type="number" />
                <YAxis dataKey="name" type="category" width={60} />
                <RechartsTooltip
                  contentStyle={{
                    backgroundColor: 'rgba(255, 255, 255, 0.95)',
                    border: '1px solid #e2e8f0',
                    borderRadius: '8px',
                  }}
                />
                <Bar dataKey="value" radius={[0, 4, 4, 0]}>
                  {jurisdictionData.map((entry, index) => (
                    <Cell key={`cell-${index}`} fill={entry.fill} />
                  ))}
                </Bar>
              </BarChart>
            </ResponsiveContainer>
          </div>
        </Card>

        {/* Request Types */}
        <Card>
          <CardTitle>Request Types</CardTitle>
          <CardDescription>Distribution of privacy request types</CardDescription>
          <div className="h-64 mt-4">
            <ResponsiveContainer width="100%" height="100%">
              <BarChart data={requestTypeData}>
                <XAxis dataKey="name" tick={{ fontSize: 11 }} />
                <YAxis />
                <RechartsTooltip
                  contentStyle={{
                    backgroundColor: 'rgba(255, 255, 255, 0.95)',
                    border: '1px solid #e2e8f0',
                    borderRadius: '8px',
                  }}
                />
                <Bar dataKey="value" fill="#3B82F6" radius={[4, 4, 0, 0]}>
                  {requestTypeData.map((_, index) => (
                    <Cell key={`cell-${index}`} fill={COLORS.requestTypes[index]} />
                  ))}
                </Bar>
              </BarChart>
            </ResponsiveContainer>
          </div>
        </Card>
      </div>

      {/* Summary Cards */}
      <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-4 mt-6">
        <Card>
          <div className="flex items-center gap-3">
            <Shield className="h-8 w-8 text-green-500" />
            <div>
              <CardDescription>Completed</CardDescription>
              <p className="text-2xl font-bold text-slate-900 dark:text-white">
                {stats.by_status.completed || 0}
              </p>
            </div>
          </div>
        </Card>
        <Card>
          <div className="flex items-center gap-3">
            <Clock className="h-8 w-8 text-blue-500" />
            <div>
              <CardDescription>In Progress</CardDescription>
              <p className="text-2xl font-bold text-slate-900 dark:text-white">
                {stats.by_status.in_progress || 0}
              </p>
            </div>
          </div>
        </Card>
        <Card>
          <div className="flex items-center gap-3">
            <Clock className="h-8 w-8 text-amber-500" />
            <div>
              <CardDescription>Received</CardDescription>
              <p className="text-2xl font-bold text-slate-900 dark:text-white">
                {stats.by_status.received || 0}
              </p>
            </div>
          </div>
        </Card>
        <Card>
          <div className="flex items-center gap-3">
            <CheckCircle className="h-8 w-8 text-green-500" />
            <div>
              <CardDescription>Verified</CardDescription>
              <p className="text-2xl font-bold text-slate-900 dark:text-white">
                {stats.by_verification.verified || 0}
              </p>
            </div>
          </div>
        </Card>
      </div>
    </PageLayout>
  )
}
